import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useI18n, useLogger, useSelector } from '~Hooks';
import {
  getDataToUpdate,
  getStaffName,
  sortParticipantsBasedOnCompType,
  isStaffParticipant,
  shouldRefetchCompetitions,
  calculateNextEventTime,
} from './utils';
import Fireworks from './Fireworks';
import Toast from './Toast';
import { CartItem } from '~Types';
import { Item as RefundItem } from '~Components/Refunds/Provider/types';
import { Item } from '@baemingo/table-events/dist/types';
import gql from 'graphql-tag';
import { useMutation, useQuery, QueryResult } from '@apollo/client';
import {
  AddToScore,
  AddToScoreVariables,
  GetCompetitions,
  GetCompetitions_Venue_competitions,
  GetCompetitions_Venue_competitions_score,
  GetCompetitions_Venue_competitions_score_sales,
  GetCompetitions_Venue_competitions_score_tips,
  GetCompetitionsVariables,
  GetStaff,
} from './__queries__';
import { COMPETITION_STATUS, COMPETITION_TYPE } from '../../../__queries__';
import { DateTime } from 'luxon';
import { useQueryWithRetries } from './useQueryWithRetries';

const COMPETITIONS_QUERY = gql`
  query GetCompetitions($venueId: ID!, $status: [COMPETITION_STATUS!]) {
    Venue(id: $venueId) {
      id
      competitions(status: $status) {
        id
        name
        type
        createdAt
        startDate
        endDate
        participants
        onlySpecificSales {
          items
          categories
        }
        score {
          staffId
          latestUpdate
          sales {
            orderCount
            itemCount
            amount
          }
          tips {
            count
            amount
          }
        }
      }
    }
  }
`;

const STAFF_QUERY = gql`
  query GetStaff {
    user {
      id
      company {
        staffs {
          id
          name
        }
      }
    }
  }
`;

const ADD_SCORE_MUTATION = gql`
  mutation AddToScore($competitionId: ID!, $staffId: ID!, $input: AddToScoreInput!) {
    addToScore(competitionId: $competitionId, staffId: $staffId, input: $input) {
      success
      error
      message
    }
  }
`;

export enum SUBTRACT_SALE_TYPE {
  REMOVE_ITEM = 'REMOVE_ITEM',
  REMOVE_CHEQUE = 'REMOVE_CHEQUE',
  REFUND_ITEM = 'REFUND_ITEM',
  REFUND_ORDER = 'REFUND_ORDER',
}

export enum SUBTRACT_TIP_TYPE {
  TIP_CORRECTION = 'TIP_CORRECTION',
  REFUND_FULL_TIP = 'TIP_REFUND_FULL',
}

interface ContextType {
  hasCompetitions: boolean;
  onGoing: GetCompetitions_Venue_competitions[];
  upcoming: GetCompetitions_Venue_competitions[];
  finished: GetCompetitions_Venue_competitions[];
  addSale: (data: { staffId: string; items: CartItem[]; tip?: number }) => void;
  subtractSale: (data: {
    staffId?: string | null;
    items: CartItem[] | Item[] | RefundItem[];
    type: SUBTRACT_SALE_TYPE;
  }) => void;
  addTip: (data: { staffId: string; tip: number }) => void;
  subtractTip: (data: { staffId: string; tip: number; type: SUBTRACT_TIP_TYPE }) => void;
  specificSales: {
    items: string[];
    categories: string[];
  };
  loading: boolean;
}

interface ProviderProps {
  children: ReactNode;
}

const Context = createContext<ContextType>({} as ContextType);

export const useCompetition = () => useContext(Context);

export default function CompetitionProvider({ children }: ProviderProps) {
  const venueId = useSelector((state) => state.venueId);
  const logger = useLogger('competitions');
  const { i18n } = useI18n();
  const [showFireworks, setShowFireworks] = useState<boolean>(false);
  const [toast, setToast] = useState<string>();

  // We only want to poll ongoing or upcoming competitions to avoid unnecessary polling
  const competitionsQuery = useQuery<GetCompetitions, GetCompetitionsVariables>(
    COMPETITIONS_QUERY,
    {
      variables: {
        venueId: venueId!,
        status: [COMPETITION_STATUS.UPCOMING, COMPETITION_STATUS.ONGOING],
      },
      skip: !venueId,
    },
  );

  const finishedCompetitionsQuery = useQuery<GetCompetitions, GetCompetitionsVariables>(
    COMPETITIONS_QUERY,
    {
      variables: { venueId: venueId!, status: [COMPETITION_STATUS.FINISHED] },
      fetchPolicy: 'cache-and-network',
      skip: !venueId,
    },
  );

  const staffQuery = useQuery<GetStaff>(STAFF_QUERY);
  const staffs = staffQuery.data?.user?.company?.staffs ?? [];
  const [addToScore] = useMutation<AddToScore, AddToScoreVariables>(ADD_SCORE_MUTATION);

  const sortCompetitions = (competitions: GetCompetitions_Venue_competitions[]) => {
    return competitions?.map((competition) => {
      const hasOnlySpecificSales =
        competition.onlySpecificSales.items.length > 0 ||
        competition.onlySpecificSales.categories.length > 0;

      const sortedScores = sortParticipantsBasedOnCompType(
        competition.score ? [...competition.score] : [],
        competition.type,
        hasOnlySpecificSales,
      );

      return {
        ...competition,
        score: sortedScores,
      };
    });
  };

  const sortedCompetitionScores = useMemo(() => {
    if (!competitionsQuery?.data?.Venue?.competitions) {
      return [];
    }
    return sortCompetitions(competitionsQuery?.data?.Venue.competitions ?? []);
  }, [competitionsQuery?.data?.Venue?.competitions, sortParticipantsBasedOnCompType]);

  const sortedFinishedCompetitionScores = useMemo(() => {
    if (!finishedCompetitionsQuery?.data?.Venue?.competitions) {
      return [];
    }
    return sortCompetitions(finishedCompetitionsQuery?.data?.Venue.competitions ?? []);
  }, [finishedCompetitionsQuery?.data?.Venue?.competitions, sortParticipantsBasedOnCompType]);

  // Memoize the ongoing competitions to avoid re-computation on every render
  const competitions: GetCompetitions_Venue_competitions[] = useMemo(() => {
    const now = DateTime.now();
    return (
      sortedCompetitionScores?.filter((competition) => {
        const startDate = DateTime.fromISO(competition.startDate);
        const endDate = DateTime.fromISO(competition.endDate);
        return startDate <= now && endDate >= now;
      }) ?? []
    );
  }, [sortedCompetitionScores]);

  // Memoize the upcoming competitions to avoid re-computation on every render
  const upcomingCompetitions: GetCompetitions_Venue_competitions[] = useMemo(() => {
    const now = DateTime.now();
    return (
      sortedCompetitionScores?.filter((competition) => {
        const startDate = DateTime.fromISO(competition.startDate);
        return startDate > now;
      }) ?? []
    );
  }, [sortedCompetitionScores]);

  // Memoize the finished competitions to avoid re-computation on every render
  const finishedCompetitions: GetCompetitions_Venue_competitions[] = useMemo(() => {
    const now = DateTime.now();
    const thirtyDaysAgo = now.minus({ days: 30 }); // We only show last 30 days of competitions in OM

    return (
      sortedFinishedCompetitionScores
        ?.filter((competition) => {
          const endDate = DateTime.fromISO(competition.endDate);
          return endDate < now && endDate >= thirtyDaysAgo;
        })
        .sort(
          (a, b) =>
            DateTime.fromISO(b.endDate).toMillis() - DateTime.fromISO(a.endDate).toMillis(),
        ) ?? []
    );
  }, [sortedFinishedCompetitionScores]);

  // Check if we need to poll or not
  useEffect(() => {
    // Start polling if there are competitions, stop if there are none
    if (competitionsQuery.data && competitions.length > 0) {
      competitionsQuery.startPolling(1000 * 30); // Poll every 30 seconds
    } else {
      competitionsQuery.stopPolling();
    }
  }, [competitionsQuery.data]);

  // Look at when the next event is (meaning startDate or endDate of a competition) and refetch competitions when it happens
  useEffect(() => {
    let lastEventTime: DateTime | null = null;

    const checkForUpdates = () => {
      const now = DateTime.now();

      if (competitions.length === 0 && upcomingCompetitions.length === 0) {
        return; // Early exit, no need to check
      }

      const nextEventTime = calculateNextEventTime(sortedCompetitionScores, now);

      if (nextEventTime) {
        const timeUntilNextEvent = nextEventTime.diff(now).as('milliseconds');

        // Refetch if time until next event has passed and the next event time is different from the last event time
        if (
          timeUntilNextEvent <= 0 &&
          nextEventTime.valueOf() !== (lastEventTime?.valueOf() ?? -1)
        ) {
          lastEventTime = nextEventTime;

          logger.info(
            'Refetching competitions due to event time passing, meaning a start- or endDate has been reached',
          );
          competitionsQuery.refetch();
          finishedCompetitionsQuery.refetch();
        }
      }
    };

    checkForUpdates();
    const intervalId = setInterval(checkForUpdates, 1000 * 10); // Check calculateNextEventTime every 10 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [
    competitions,
    upcomingCompetitions,
    sortedCompetitionScores,
    competitionsQuery,
    finishedCompetitionsQuery,
  ]);

  const checkPosition = (
    staffId: string,
    competition: GetCompetitions_Venue_competitions,
    newScore: {
      sales?: GetCompetitions_Venue_competitions_score_sales;
      tips?: GetCompetitions_Venue_competitions_score_tips;
    },
    hasMultipleCompetitions: boolean,
  ) => {
    const prevScores = competition.score ? [...competition.score] : [];

    const newScores: GetCompetitions_Venue_competitions_score[] = prevScores.map((score) => {
      if (score.staffId === staffId) {
        return {
          ...score,
          sales: {
            ...score.sales,
            orderCount: newScore.sales ? newScore.sales.orderCount : score.sales.orderCount,
            itemCount: newScore.sales ? newScore.sales.itemCount : score.sales.itemCount,
            amount: newScore.sales ? newScore.sales.amount : score.sales.amount,
          },
          tips: {
            ...score.tips,
            count: newScore.tips ? newScore.tips.count : score.tips.count,
            amount: newScore.tips ? newScore.tips.amount : score.tips.amount,
          },
        };
      }
      return score;
    });

    const hasOnlySpecificSales =
      competition.onlySpecificSales.items.length > 0 ||
      competition.onlySpecificSales.categories.length > 0;

    const sortedNewScores = sortParticipantsBasedOnCompType(
      newScores,
      competition.type,
      hasOnlySpecificSales,
    );

    const tookFirstPlace =
      sortedNewScores.length > 0 &&
      sortedNewScores[0].staffId === staffId &&
      prevScores[0]?.staffId !== staffId;

    if (tookFirstPlace) {
      setShowFireworks(true);
      setTimeout(() => {
        if (hasMultipleCompetitions) {
          setToast(i18n('Competitions.FirstPlace.CompName', competition.name));
        } else {
          setToast(i18n('Competitions.FirstPlace'));
        }
      }, 1000);
      setTimeout(() => {
        setShowFireworks(false);
        setToast(undefined);
      }, 6000);
      return;
    }

    const staffDidAnOvertake =
      sortedNewScores.findIndex((score) => score.staffId === staffId) <
      prevScores.findIndex((score) => score.staffId === staffId);

    if (staffDidAnOvertake) {
      const newIndex = sortedNewScores.findIndex((score) => score.staffId === staffId);
      const prevIndexStaff = prevScores[newIndex];

      if (prevIndexStaff) {
        if (hasMultipleCompetitions) {
          setToast(
            i18n('Competitions.Overtook', getStaffName(prevIndexStaff.staffId, staffs, i18n)) +
              i18n('Competitions.Overtook.In', competition.name),
          );
        } else {
          setToast(
            i18n('Competitions.Overtook', getStaffName(prevIndexStaff.staffId, staffs, i18n)) +
              '.',
          );
        }
        setTimeout(() => {
          setToast(undefined);
        }, 4000);
      }
    }
  };

  // Perform mutation and enable retries if the mutation fails
  const performWithRetries = async (
    operation: () => Promise<any>,
    operationName: string,
  ): Promise<any | null> => {
    const retries = 3;
    const delay = 1000; // 1 second

    for (let i = 0; i < retries; i++) {
      try {
        return await operation();
      } catch (error) {
        if (i < retries - 1) {
          logger.warn(`Failed attempt ${i + 1} for ${operationName}. Retrying...`, {
            errorObject: error,
          });
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          logger.error(`All ${retries} attempts failed for ${operationName}`, {
            errorObject: error,
          });
        }
      }
    }
    return null;
  };

  const addSale = useCallback(
    async ({ staffId, items, tip }: { staffId: string; items: CartItem[]; tip?: number }) => {
      if (!competitions.length) return;

      for (const competition of competitions) {
        if (!isStaffParticipant(competition, staffId)) {
          continue;
        }

        const data = getDataToUpdate(competition, items);
        const foundPrevScore =
          competition.score && competition.score.find((score) => score.staffId === staffId);

        if (data.amount === 0 && data.itemCount === 0) {
          continue;
        }

        const resultAfterRetries = await performWithRetries(async () => {
          const result = await addToScore({
            variables: {
              competitionId: competition.id,
              staffId,
              input: {
                sales: {
                  orderCount: 1,
                  itemCount: data.itemCount,
                  amount: data.amount,
                },
                tips: {
                  count: tip ? 1 : 0,
                  amount: tip ?? 0,
                },
              },
            },
          });

          if (!result?.data) {
            throw new Error(result?.data?.addToScore?.error);
          }

          return result;
        }, 'addSale');

        if (resultAfterRetries) {
          checkPosition(
            staffId,
            competition,
            {
              sales: {
                __typename: 'SalesScore',
                orderCount: (foundPrevScore?.sales?.orderCount ?? 0) + 1,
                itemCount: (foundPrevScore?.sales?.itemCount ?? 0) + data.itemCount,
                amount: (foundPrevScore?.sales?.amount ?? 0) + data.amount,
              },
              tips: {
                __typename: 'TipScore',
                count: (foundPrevScore?.tips?.count ?? 0) + 1,
                amount: (foundPrevScore?.tips?.amount ?? 0) + (tip ?? 0),
              },
            },
            competitions.length > 1,
          );

          setTimeout(() => {
            competitionsQuery.refetch();
          }, 100);
        }
      }
    },
    [competitions],
  );

  const subtractSale = useCallback(
    async ({
      staffId,
      items,
      type,
    }: {
      staffId?: string | null;
      items: CartItem[] | Item[] | RefundItem[];
      type: SUBTRACT_SALE_TYPE;
    }) => {
      if (!competitions.length) return;

      if (!staffId) {
        logger.error('No ownerId was provided when trying to subtract sale');
        return;
      }

      for (const competition of competitions) {
        if (!isStaffParticipant(competition, staffId)) {
          continue;
        }

        const data = getDataToUpdate(competition, items);

        if (data.amount === 0 && data.itemCount === 0) {
          continue;
        }

        const decrementOrderCount =
          type === SUBTRACT_SALE_TYPE.REMOVE_CHEQUE || type === SUBTRACT_SALE_TYPE.REFUND_ORDER;

        const resultAfterRetries = await performWithRetries(async () => {
          const result = await addToScore({
            variables: {
              competitionId: competition.id,
              staffId,
              input: {
                sales: {
                  orderCount: decrementOrderCount ? -1 : 0,
                  itemCount: -data.itemCount,
                  amount: -data.amount,
                },
              },
            },
          });

          if (!result?.data) {
            throw new Error(result?.data?.addToScore?.error);
          }

          return result;
        }, 'subtractSale');

        if (resultAfterRetries) {
          setTimeout(() => {
            competitionsQuery.refetch();
          }, 100);
        }
      }
    },
    [competitions],
  );

  const addTip = useCallback(
    async ({ staffId, tip }: { staffId: string; tip: number }) => {
      if (!competitions.length) return;

      for (const competition of competitions) {
        if (!isStaffParticipant(competition, staffId)) {
          continue;
        }
        const foundPrevScore =
          competition.score && competition.score.find((score) => score.staffId === staffId);

        if (tip === 0) {
          continue;
        }

        const resultAfterRetries = await performWithRetries(async () => {
          const result = await addToScore({
            variables: {
              competitionId: competition.id,
              staffId,
              input: {
                tips: {
                  count: 1,
                  amount: tip,
                },
              },
            },
          });

          if (!result?.data) {
            throw new Error(result?.data?.addToScore?.error);
          }

          return result;
        }, 'addTip');

        if (resultAfterRetries) {
          if (
            competition.type === COMPETITION_TYPE.TIPS_AMOUNT ||
            competition.type === COMPETITION_TYPE.TIPS_COUNT ||
            competition.type === COMPETITION_TYPE.TIPS_AVERAGE
          ) {
            checkPosition(
              staffId,
              competition,
              {
                tips: {
                  __typename: 'TipScore',
                  count: (foundPrevScore?.tips?.count ?? 0) + 1,
                  amount: (foundPrevScore?.tips?.amount ?? 0) + tip,
                },
              },
              competitions.length > 1,
            );
          }

          setTimeout(() => {
            competitionsQuery.refetch();
          }, 100);
        }
      }
    },
    [competitions],
  );

  const subtractTip = useCallback(
    async ({ staffId, tip, type }: { staffId: string; tip: number; type: SUBTRACT_TIP_TYPE }) => {
      if (!competitions.length) return;

      for (const competition of competitions) {
        if (!isStaffParticipant(competition, staffId)) {
          continue;
        }

        const decrementTipCount = SUBTRACT_TIP_TYPE.REFUND_FULL_TIP === type;

        if (tip <= 0) {
          continue;
        }

        const resultAfterRetries = await performWithRetries(async () => {
          const result = await addToScore({
            variables: {
              competitionId: competition.id,
              staffId,
              input: {
                tips: {
                  count: decrementTipCount ? -1 : 0,
                  amount: -tip,
                },
              },
            },
          });

          if (!result?.data) {
            throw new Error(result?.data?.addToScore?.error);
          }

          return result;
        }, 'subtractTip');

        if (resultAfterRetries) {
          setTimeout(() => {
            competitionsQuery.refetch();
          }, 100);
        }
      }
    },
    [competitions],
  );

  const combinedOnlySpecificSales = [...competitions]
    .filter((comp) => comp.onlySpecificSales)
    .reduce(
      (acc, comp) => {
        return {
          categories: [...acc.categories, ...(comp.onlySpecificSales?.categories ?? [])],
          items: [...acc.items, ...(comp.onlySpecificSales?.items ?? [])],
        };
      },
      {
        categories: [] as string[],
        items: [] as string[],
      },
    );

  // Remove duplicates using Set and Array.from
  combinedOnlySpecificSales.categories = Array.from(new Set(combinedOnlySpecificSales.categories));
  combinedOnlySpecificSales.items = Array.from(new Set(combinedOnlySpecificSales.items));

  // If it doesnt exist any competitions, we don't want to show the navigation in Handy/Home
  const hasNotCreatedOneCompetition =
    competitions.length === 0 &&
    upcomingCompetitions.length === 0 &&
    finishedCompetitions.length === 0;

  const contextValue = {
    hasCompetitions: !hasNotCreatedOneCompetition,
    onGoing: competitions ?? [],
    upcoming: upcomingCompetitions ?? [],
    finished: finishedCompetitions ?? [],
    addSale,
    subtractSale,
    addTip,
    subtractTip,
    specificSales: combinedOnlySpecificSales,
    loading: competitionsQuery.loading || staffQuery.loading,
  };

  return (
    <Context.Provider value={contextValue}>
      {showFireworks && <Fireworks />}
      {children}
      {toast && <Toast message={toast} />}
    </Context.Provider>
  );
}
